/** Выпуск готового отчёта (АН-07): страница, Word и PDF собраны из одного снимка данных */
import type { DocColumn, DocSection, ReportRunFull } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { FileText, FileType2 } from 'lucide-react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime, percent, tenge } from '../../lib/format';
import { useCan } from '../../lib/session';
import { Badge, ErrorBox, Spinner } from '../../ui/feedback';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { RecordLink } from './ResultView';

const numFmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

function fmt(col: DocColumn, v: string | number | null): string {
  if (v === null || v === '') return '—';
  if (typeof v === 'number') {
    if (col.format === 'money') return tenge(v);
    if (col.format === 'pct') return percent(v);
    if (col.format === 'date') return date(String(v));
    return numFmt.format(v);
  }
  return col.format === 'date' && /^\d{4}-\d{2}-\d{2}/.test(v) ? date(v) : v;
}

function Section({ s }: { s: DocSection }) {
  return (
    <Panel title={s.heading} bodyClassName="p-0">
      {s.paragraphs.length ? (
        <div className="flex flex-col gap-2 px-5 py-3 text-[14px]">
          {s.paragraphs.map((p) => (
            <p key={p}>{p}</p>
          ))}
        </div>
      ) : null}
      {s.kpis.length ? (
        <dl className="grid grid-cols-2 gap-3 px-5 py-3 md:grid-cols-4">
          {s.kpis.map((k) => (
            <div key={k.label} className="rounded-md border border-line px-3 py-2">
              <dt className="text-[12px] text-ink-2">{k.label}</dt>
              <dd className="num text-[16px] font-semibold">{k.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {s.table ? (
        <>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[640px] text-[13px]">
              <thead>
                <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                  {s.table.columns.map((c) => (
                    <th
                      key={c.key}
                      scope="col"
                      className={clsx('px-3 py-2 font-medium', c.align === 'right' && 'text-right')}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {s.table.rows.map((row, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: строки документа без ключа
                  <tr key={i} className="border-t border-line align-top">
                    {s.table?.columns.map((c, j) => (
                      <td
                        key={c.key}
                        className={clsx(
                          'px-3 py-2 [overflow-wrap:anywhere]',
                          c.align === 'right' && 'num text-right',
                        )}
                      >
                        {j === 0 ? (
                          <RecordLink href={s.table?.links[i]}>{fmt(c, row[j] ?? null)}</RecordLink>
                        ) : (
                          fmt(c, row[j] ?? null)
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="divide-y divide-line md:hidden">
            {s.table.rows.map((row, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: строки документа без ключа
              <li key={i} className="px-4 py-3 text-[13px]">
                <div className="font-medium [overflow-wrap:anywhere]">
                  <RecordLink href={s.table?.links[i]}>
                    {s.table ? fmt(s.table.columns[0] as DocColumn, row[0] ?? null) : ''}
                  </RecordLink>
                </div>
                <dl className="mt-1 grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-x-3 gap-y-0.5">
                  {s.table?.columns.slice(1).map((c, j) => (
                    <div key={c.key} className="contents">
                      <dt className="text-ink-2">{c.label}</dt>
                      <dd className="[overflow-wrap:anywhere]">{fmt(c, row[j + 1] ?? null)}</dd>
                    </div>
                  ))}
                </dl>
              </li>
            ))}
          </ul>
          {!s.table.rows.length ? <p className="px-5 py-3 text-[13px] text-ink-2">Записей нет.</p> : null}
        </>
      ) : null}
    </Panel>
  );
}

const btn =
  'inline-flex h-11 items-center gap-1.5 rounded-md border border-line-strong bg-surface px-3 text-[13px] font-medium text-ink hover:underline md:h-8';

export function RunPage() {
  const mayExport = useCan('analytics.report.export');
  const { id } = useParams({ strict: false }) as { id: string };
  const q = useQuery({
    queryKey: ['reports', 'run', id],
    queryFn: () => api<ReportRunFull>(`/reports/ready/runs/${id}`),
  });
  useCrumbs(q.data ? [{ label: 'Отчёты', to: '/reports' }, { label: q.data.number }] : null, q.error);
  if (q.isPending) return <Spinner />;
  if (q.isError)
    return (
      <Page>
        <PageHeader title="Выпуск отчёта" />
        <ErrorBox error={q.error} />
      </Page>
    );
  const r = q.data;
  return (
    <Page>
      <PageHeader
        title={r.doc.title}
        subtitle={
          <>
            {r.doc.subtitle ? <span className="block">{r.doc.subtitle}</span> : null}
            <span className="block">
              <span className="num">{r.number}</span> · на {date(r.asOf)} · выпущен {dateTime(r.createdAt)}
              {r.createdByName ? `, ${r.createdByName}` : ''}{' '}
              {!r.moneyShown ? <Badge tone="neutral">без сумм</Badge> : null}
            </span>
          </>
        }
        actions={
          mayExport ? (
            <>
              <a className={btn} href={`/api/v1/reports/ready/runs/${id}/export?format=docx`}>
                <FileType2 className="size-4" /> Word
              </a>
              <a className={btn} href={`/api/v1/reports/ready/runs/${id}/export?format=pdf`}>
                <FileText className="size-4" /> PDF
              </a>
            </>
          ) : undefined
        }
      />
      {r.doc.notes.length ? (
        <ul className="mb-6 list-disc rounded-lg border border-line bg-surface py-3 pr-5 pl-9 text-[13px] text-ink-2">
          {r.doc.notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      ) : null}
      <div className="flex flex-col gap-6">
        {r.doc.sections.map((s) => (
          <Section key={s.heading} s={s} />
        ))}
      </div>
    </Page>
  );
}
